import { OktaAuth } from '@okta/okta-auth-js';
import moment from 'moment';
import { tokenHasExpired } from '../../helpers/auth';
import { returnCurrencySymbol, returnFormattedSegmentFilters } from '../../helpers/format';
import { store } from '../../redux/store';
import { fetchCall } from '../../services/api';
import { ModalFilter, ScratchpadParams, MultiMetricData, MultiMetricValue } from '../../types';

export const returnParams = (marketFilters: Array<ModalFilter | null>, dateFilters: string): any => {
  const segmentFilters = returnFormattedSegmentFilters(marketFilters, true);
  const formattedDates = dateFilters.split(' - ');
  const params = {
    ...segmentFilters,
    // sets date to ISO format YYYY-MM-DD (refer to OpenAPI specs)
    startDate: moment(new Date(formattedDates[0])).format('YYYY-MM-DD'),
    endDate: moment(new Date(formattedDates[1])).format('YYYY-MM-DD')
  };

  return params;
};

/**
 * Handles total overview data fetching and aggregation
 * @param marketFilters
 * @param dateFilters
 * @returns {Promise<any>} promise value with summary data
 */

export async function fetchMultiMetric(params: ScratchpadParams, oktaAuth: OktaAuth): Promise<any> {
  const api = store.getState().config.react_app_api_base_url;
  return new Promise((resolve, reject) => {
    try {
      const response = fetchCall(`${api}/scratchpad/multimetric`, params);
      oktaAuth?.tokenManager.get('idToken').then((idToken) => {
        if (Date.now() + 3600 * 1000 - idToken.expiresAt * 1000 > 60000) {
          tokenHasExpired(200, oktaAuth);
        }
      });
      resolve(response);
    } catch (error) {
      console.error(error);
      if ((error as any)?.response.status == 401) tokenHasExpired((error as any)?.response.status, oktaAuth);
      reject('Error fetching Multi Metric data. Please try again.');
    }
  });
}

export async function fetchSlicerDimension(params: ScratchpadParams): Promise<any> {
  const api = store.getState().config.react_app_api_base_url;
  return new Promise((resolve, reject) => {
    try {
      resolve(fetchCall(`${api}/scratchpad/dimensions`, params));
    } catch (error) {
      console.error(error);
      reject('Error fetching Slicer dimensions data. Please try again.');
    }
  });
}

export async function fetchSlicer(
  params: ScratchpadParams,
  sliceBy: string,
  sliceByMetric: string,
  oktaAuth: OktaAuth
): Promise<any> {
  const api = store.getState().config.react_app_api_base_url;
  return new Promise((resolve, reject) => {
    try {
      const response = fetchCall(`${api}/scratchpad/slicer/${sliceBy}/${sliceByMetric}`, params);
      oktaAuth?.tokenManager.get('idToken').then((idToken) => {
        if (Date.now() + 3600 * 1000 - idToken.expiresAt * 1000 > 60000) {
          tokenHasExpired(200, oktaAuth);
        }
      });
      resolve(response);
    } catch (error) {
      console.error(error);
      if ((error as any)?.response.status == 401) tokenHasExpired((error as any)?.response.status, oktaAuth);
      reject('Error fetching Slicer data. Please try again.');
    }
  });
}

//parse data
export const parseMultiMetricData = (data: Array<MultiMetricData>): Array<MultiMetricData> => {
  let metrics = [...data];
  metrics = metrics.map((metric: MultiMetricData) => {
    const color = getColor(metric.color);
    metric.color = color;
    metric.visible = true;
    metric.graphVisible = true;
    return metric;
  });
  return metrics;
};

const getColor = (color?: string) => {
  return color || '#' + Math.floor(Math.random() * 16777215).toString(16); //random color if none is coming from the backend
};

//parse data for csv and xls export
export const parseExportData = (
  data: Array<MultiMetricData>,
  selectedCurrency: string,
  valueOrPercentage?: string
): Array<any> => {
  const isPercentage = valueOrPercentage && valueOrPercentage === 'percentage' ? true : false;
  const dates = getDatesArrayFromData(data);
  if (dates && dates.length) {
    const dateArray: any = dates.map((date) => {
      return {
        date: date
      };
    });

    data.forEach((dataObject) => {
      if (dataObject.visible && dataObject.graphVisible) {
        dataObject.valuesWithDates.forEach((date) => {
          const dateObjects = dateArray.filter((d: any) => d.date === date.date);
          if (date.date === dateObjects[0].date) {
            const valueArray = dateObjects[0];
            const value = isPercentage && date.percentage ? date.percentage : date.value;
            const symbol =
              dataObject.currentValueType === 'scalar' && !isPercentage
                ? ''
                : isPercentage || dataObject.currentValueType === 'percentage'
                ? ' (%)'
                : ` (${returnCurrencySymbol(selectedCurrency)})`;
            valueArray[dataObject.label + symbol] = value;
          }
        });
      }
    });
    return dateArray;
  } else {
    return data;
  }
};

export const getDatesArrayFromData = (data: MultiMetricData[]): Array<string> | null => {
  //create new array with all possible dates in dimension valuesWithDates
  const dataCopy = [...data];
  if (dataCopy && dataCopy.length) {
    const initialDates = dataCopy[0].valuesWithDates.map((v: MultiMetricValue) => v.date);
    const dates: string[] = [...initialDates];
    dataCopy.forEach((item) => {
      item.valuesWithDates.forEach((date: any) => {
        if (!dates.includes(date.date)) dates.push(date.date);
      });
    });

    dates.sort();
    return dates;
  } else {
    return null;
  }
};
